import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Edit } from '@mui/icons-material';
import { Button, Checkbox, TextField } from '@mui/material';
import { useDebouncedCallback } from 'use-debounce';

import * as S from 'commons/components/Table/Table.styles';
import { STODataTableProps, STOItem } from 'commons/types';
import {
  stoCheckItemAction,
  stoCheckUpdateBoxNumberAndPallet,
  stoCheckUpdateCheckedQty,
} from 'redux-stores/actions';
import { RootReducerInterface } from 'redux-stores/reducers';
import { AppDispatch } from 'redux-stores/store';

import { CheckBasketStyle as SB } from '../STOCheckBasket.styles';

import { BasketInfoDataTable } from './BasketInfoDataTable.component';
import EditQtyModal from './EditQtyModal.component';

const Row: React.FC<{
  data: STOItem;
  onCheckboxClick: (itemIndex: number) => void;
  itemIndex: number;
  basketNumber: number;
  getTotalQtyBySkuCode: (skuCode: string) => number;
  isDisabled: boolean;
}> = ({
  data,
  onCheckboxClick,
  itemIndex,
  basketNumber,
  getTotalQtyBySkuCode,
  isDisabled,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const { stoCheckBasket } = useSelector(
    (state: RootReducerInterface) => state.stoCheckBasket,
  );
  const [open, setOpen] = useState<boolean>(false);
  const [qtyChecked, setQtyChecked] = useState<number>(
    data.checking_metadata.total_qty_checked || 0,
  );

  const [boxNumber, setBoxNumber] = useState<string>(data.box_number || '');
  const [pallet, setPallet] = useState<string>(data.pallet || '');

  const handleOpenDialog = (): void => {
    setOpen(true);
  };

  const handleCloseDialog = (): void => {
    setOpen(false);
  };

  const debouncedUpdateQtyChecked = useDebouncedCallback(
    async () => {
      dispatch(
        stoCheckUpdateCheckedQty({
          stoNumber: stoCheckBasket?.sto_number || '',
          basket: basketNumber,
          payload: {
            ...data,
            checking_metadata: {
              ...data.checking_metadata,
              total_qty_checked: qtyChecked,
            },
          },
        }),
      );
    },
    500,
    { maxWait: 2000, trailing: true },
  );

  const debouncedUpdateBoxNumberAndPallet = useDebouncedCallback(
    async () => {
      dispatch(
        stoCheckUpdateBoxNumberAndPallet({
          stoNumber: stoCheckBasket?.sto_number || '',
          basket: basketNumber,
          payload: {
            ...data,
            box_number: boxNumber,
            pallet,
          },
        }),
      );
    },
    500,
    { maxWait: 2000, trailing: true },
  );

  const onFieldChange = (
    type: keyof STOItem | 'total_qty_checked',
    value: string,
  ): void => {
    if (type === 'box_number') {
      setBoxNumber(value);
      debouncedUpdateBoxNumberAndPallet();
    } else if (type === 'pallet') {
      setPallet(value);
      debouncedUpdateBoxNumberAndPallet();
    } else if (type === 'total_qty_checked') {
      if (value === '') {
        setQtyChecked(0);
        return;
      }
      const newQtyChecked = value && +value > 0 ? +value : 0;
      setQtyChecked(newQtyChecked);
      debouncedUpdateQtyChecked();
    }
  };

  return (
    <>
      <S.TableRow>
        <S.TableColumn>
          <SB.BoldText>{data.sku_code}</SB.BoldText>
          {data.checking_metadata.is_modified ? (
            <SB.GreenText data-testid="labelCheckModified">
              Modified
            </SB.GreenText>
          ) : null}
        </S.TableColumn>
        <S.TableColumn>{data.name}</S.TableColumn>
        <S.TableColumn>{data.uom}</S.TableColumn>
        <S.TableColumn style={{ textAlign: 'center' }}>
          {data.quantity}
        </S.TableColumn>
        <S.TableColumn style={{ textAlign: 'center', width: '69px' }}>
          <TextField
            variant="outlined"
            type="number"
            value={qtyChecked || ''}
            onChange={(event): void =>
              onFieldChange('total_qty_checked', event.target.value)
            }
            disabled={isDisabled}
            data-testid="CheckedQTYField"
          />
        </S.TableColumn>
        <S.TableColumn style={{ textAlign: 'center', width: '69px' }}>
          <TextField
            variant="outlined"
            value={boxNumber || ''}
            onChange={(event): void =>
              onFieldChange('box_number', event?.target.value)
            }
            disabled={isDisabled}
            data-testid="BoxNumberField"
          />
        </S.TableColumn>
        <S.TableColumn style={{ textAlign: 'center', width: '69px' }}>
          <TextField
            variant="outlined"
            value={pallet || ''}
            onChange={(event): void =>
              onFieldChange('pallet', event?.target.value)
            }
            disabled={isDisabled}
            data-testid="PalletField"
          />
        </S.TableColumn>
        <S.TableColumn style={{ textAlign: 'center' }}>
          <Checkbox
            onClick={() => onCheckboxClick(itemIndex)}
            checked={data.pick_actual.every(
              (pickActual) => pickActual.check_passed,
            )}
            disabled={
              getTotalQtyBySkuCode(data.sku_code) > data.quantity || isDisabled
            }
            inputProps={
              {
                'data-testid': `checkPassed-${basketNumber}-${itemIndex + 1}`,
              } as React.InputHTMLAttributes<HTMLInputElement>
            }
          />
        </S.TableColumn>
        <S.TableColumn>
          <Button onClick={() => handleOpenDialog()}>
            <Edit />
          </Button>
        </S.TableColumn>
      </S.TableRow>
      <EditQtyModal
        openModal={open}
        onClose={() => handleCloseDialog()}
        data={data}
        basketNumber={basketNumber}
      />
    </>
  );
};

export const BasketInfoTable: React.FC<STODataTableProps> = (
  params,
): React.ReactNode => {
  const {
    stoNumber,
    data = [],
    rowTitles,
    basketNumber,
    getTotalQtyBySkuCode,
    isDisabled,
  }: STODataTableProps = params;

  const dispatch = useDispatch<AppDispatch>();

  const handleCheckPassed = (itemIndex: number): void => {
    dispatch(
      stoCheckItemAction({
        stoNumber,
        basket: basketNumber,
        skuCode: data[itemIndex].sku_code,
        isChecked: !data[itemIndex].pick_actual.every(
          (pickActual) => pickActual.check_passed,
        ),
        itemIndex,
      }),
    );
  };

  return (
    <BasketInfoDataTable rowTitles={rowTitles}>
      <S.TableBody>
        {data.map((item, index) => (
          <Row
            data={item}
            key={item.sku_code}
            onCheckboxClick={handleCheckPassed}
            itemIndex={index}
            getTotalQtyBySkuCode={getTotalQtyBySkuCode}
            basketNumber={basketNumber}
            isDisabled={isDisabled}
          />
        ))}
      </S.TableBody>
    </BasketInfoDataTable>
  );
};
